import { useUploadLogos } from "api/image.api";
import { FileUploadButton } from "components/buttons/FileUploadButton";
import { useNotification } from "hooks/useNotification";
import { FC, useCallback, useEffect, useState } from "react";
import { Company } from "types/company.type";
import SecondaryButton from "components/buttons/SecondaryButton";
import { UpdateCompanyDTO, useUpdateCompany } from "api/company.api";
import { useForm } from "react-hook-form";
import Modal from "components/Modal";
import { EditCompanyModal } from "./EditCompanyModal";
import {
  BuildingOffice2Icon,
  PencilSquareIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";
import {
  Card,
  CardBody,
  CardHeader,
  DetailList,
  DetailRow,
  MicroLabel,
} from "./ui";

interface Props {
  company: Company;
}

export const OverviewTab: FC<Props> = ({ company }) => {
  const [isEditCompanyModalOpen, setIsEditCompanyModalOpen] =
    useState<boolean>(false);

  const {
    mutate: updateCompany,
    isSuccess: isUpdateCompanySuccess,
    isPending: isUpdatingCompany,
  } = useUpdateCompany(company.id);

  const {
    register: registerCompany,
    handleSubmit: handleUpdateCompanySubmit,
    reset: resetCompany,
    formState: { errors: updateCompanyErrors },
  } = useForm<UpdateCompanyDTO>();
  const onUpdateCompanySubmit = handleUpdateCompanySubmit(
    (data: UpdateCompanyDTO) => updateCompany(data),
  );

  const resetForm = useCallback(
    (company: Company) => {
      if (company) {
        resetCompany(company);
      } else {
        resetCompany(); // Tror aldrig man kommer hit men men
      }
    },
    [resetCompany],
  );

  useEffect(() => {
    document.title = "Organisation";
  }, []);

  useEffect(() => {
    if (company) {
      resetForm(company);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company]);

  useEffect(() => {
    if (isUpdateCompanySuccess) {
      setIsEditCompanyModalOpen(false);
    }
  }, [isUpdateCompanySuccess]);

  const onEditCompanyModalClose = () => {
    setIsEditCompanyModalOpen(false);
    setTimeout(() => {
      if (company) {
        resetForm(company);
      }
    }, 300); // Wait for modal to close
  };

  const setLogoUrl = (logoUrl: string) => {
    if (!company) return;
    const { id, ...rest } = company;
    updateCompany({ ...rest, logoUrl });
  };

  const setCertificateLogoUrl = (certificateLogoUrl: string) => {
    if (!company) return;
    const { id, ...rest } = company;
    updateCompany({ ...rest, certificateLogoUrl });
  };

  const hasAddress =
    !!company.address || !!company.postalCode || !!company.city;
  const hasReportingContact =
    !!company.contactFullName ||
    !!company.contactEmail ||
    !!company.contactPhone;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader
          icon={BuildingOffice2Icon}
          title="Företagsuppgifter"
          description="Uppgifterna används på kontrollrapporter och vid rapportering."
          action={
            <SecondaryButton
              size="small"
              onClick={() => setIsEditCompanyModalOpen(true)}
            >
              <PencilSquareIcon className="mr-1.5 h-4 w-4" />
              Ändra
            </SecondaryButton>
          }
        />
        <CardBody>
          <DetailList>
            <DetailRow label="Namn på ert företag">{company.name}</DetailRow>
            <DetailRow label="Organisationsnummer">
              <span className="font-mono">{company.organizationNumber}</span>
            </DetailRow>
            <DetailRow label="Adress">
              {hasAddress && (
                <div className="space-y-0.5">
                  {!!company.address && <p>{company.address}</p>}
                  {!!company.postalCode && <p>{company.postalCode}</p>}
                  {!!company.city && <p>{company.city}</p>}
                </div>
              )}
            </DetailRow>
            <DetailRow label="Kontaktperson">
              {hasReportingContact && (
                <div className="space-y-0.5">
                  {!!company.contactFullName && (
                    <p>{company.contactFullName}</p>
                  )}
                  {!!company.contactEmail && (
                    <p className="text-slate-500">{company.contactEmail}</p>
                  )}
                  {!!company.contactPhone && (
                    <p className="text-slate-500">{company.contactPhone}</p>
                  )}
                </div>
              )}
            </DetailRow>
          </DetailList>
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          icon={PhotoIcon}
          title="Loggor"
          description="Visas på era kontrollrapporter och transportdokument."
          accent="violet"
        />
        <CardBody className="grid gap-4 sm:grid-cols-2">
          <FileZone label="Logga" url={company.logoUrl} setUrl={setLogoUrl} />
          <FileZone
            label="Certifiering / ackreditering"
            url={company.certificateLogoUrl}
            setUrl={setCertificateLogoUrl}
          />
        </CardBody>
      </Card>

      <Modal
        isOpen={isEditCompanyModalOpen}
        onClose={onEditCompanyModalClose}
        title="Ändra företagets inställningar"
        actionText="Spara ändringar"
        actionHandler={onUpdateCompanySubmit}
        isLoading={isUpdatingCompany}
        loadingText="Sparar ändringar..."
      >
        <EditCompanyModal
          register={registerCompany}
          errors={updateCompanyErrors}
        />
      </Modal>
    </div>
  );
};

type FileZoneProps = {
  label: string;
  url?: string;
  setUrl: (url: string) => void;
};
const FileZone = ({ label, url, setUrl }: FileZoneProps) => {
  const mutation = useUploadLogos();

  const onLogoAdd = async (blob: Blob | null, filename: string) => {
    if (!blob) return;
    const file = new File([blob], filename);
    if (!file) {
      return;
    }

    mutation.mutate([file]);
  };

  useEffect(() => {
    if (mutation.isSuccess && mutation.data) {
      setUrl(mutation.data[0]);
      mutation.reset();
    }
  }, [mutation, setUrl]);

  const uploadedUrl =
    !!mutation.data && mutation.data?.length > 0 ? mutation.data[0] : "";
  const previewUrl = url ?? uploadedUrl;

  return (
    <div className="rounded-xl border border-slate-200 p-4">
      <MicroLabel>{label}</MicroLabel>
      <div className="mt-3 flex h-20 items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/70 p-2">
        {previewUrl ? (
          <img
            className="max-h-16 max-w-full object-contain"
            src={previewUrl}
            alt={label}
          />
        ) : (
          <span className="text-xs text-slate-400">Ingen logga uppladdad</span>
        )}
      </div>
      <div className="mt-3">
        <FileUploadButton
          id={label}
          onFileAdd={onLogoAdd}
          isLoading={mutation.isPending}
          accept="image/*,image/heic"
          label={url ? "Byt logga" : "Ladda upp"}
        />
      </div>
    </div>
  );
};
